import { useEffect, useState } from "react";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import {
  ExperienceAnalyticsForDay,
  ExperienceAnalyticsForDaySummary,
  ExperienceAnalyticsLocalRecordingsByHour,
  ExperienceAnalyticsSessionsByHour,
  ExperienceAnalyticsSummary,
} from "../../types/Analytics";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAnalyticsService from "../../services/AnalyticsService";
import useExperienceService from "../../services/ExperienceService";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import ExperienceAnalyticsBlock from "../../components/Analytics/ExperienceAnalyticsBlock";
import { Experience } from "../../types/Experience";
import Ghost from "../../components/Ghost";

export default function ExperienceAnalyticsPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingHourlyData, setIsGettingHourlyData] = useState(false);
  const [hourlyData, setHourlyData] = useState<ExperienceAnalyticsForDay | null>(null);
  const [experienceSummary, setExperienceSummary] = useState<ExperienceAnalyticsSummary | null>(null);
  const [hourlyDataSummary, setHourlyDataSummary] = useState<ExperienceAnalyticsForDaySummary | null>(null);
  const [experience, setExperience] = useState<Experience | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the experience ID from the URL: analytics/experience/:experienceId
  const { companyId, experienceId } = useParams();

  const { getExperienceSummary, getExperienceAnalyticsByHour } =
    useAnalyticsService();
  const { getExperience } = useExperienceService();

  const now = new Date();
  const currentDate = now.toISOString().split("T")[0]; // Format: YYYY-MM-DD

  // Initialize formData with search params from URL if available, else use defaults
  const [formData, setFormData] = useState({
    date: currentDate,
  });

  const buildHourlyDataSummary = (data: ExperienceAnalyticsForDay) => {
    const totalLocalRecordings = data.localRecordings.reduce(
      (acc, item) => acc + item.count,
      0
    );
    const totalSessions = data.sessions.reduce(
      (acc, item) => acc + item.count,
      0
    );

    // Calculate the average duration for local recordings where the duration is not 0.
    const averageLocalRecordingDuration : number =
      totalLocalRecordings > 0
        ? (
            data.localRecordings.reduce(
              (acc, item) => acc + item.averageDuration * item.count,
              0
            ) / totalLocalRecordings
          )
        : 0;

    const averageSessionDuration : number =
      totalSessions > 0
        ? (
            data.sessions.reduce(
              (acc, item) => acc + item.averageDuration * item.count,
              0
            ) / totalSessions
          )
        : 0;

    setHourlyDataSummary({
      totalLocalRecordings,
      totalSessions,
        averageLocalRecordingDuration,
        averageSessionDuration,
    });
  }

  const getHourlyData = () => {
    setIsGettingHourlyData(true);
    const date = formData.date;

    getExperienceAnalyticsByHour(companyId!, experienceId!, date)
      .then((data) => {
        buildHourlyDataSummary(data);
        setHourlyData(data);
        setIsGettingHourlyData(false);
      })
      .catch((error) => {
        console.error("Error getting hourly data:", error);
        setIsGettingHourlyData(false);
      });
  };

  const formatDateAsTime = (date: Date) => {
    var date = new Date(date.toString());

    // Add 1 hour to the date
    var plusOneHour = new Date(date.getTime() + 1 * 60 * 60 * 1000);

    // Return the pair in the format 01:00 - 02:00
    return `${date.toTimeString().split(" ")[0].slice(0, 5)} - ${plusOneHour
      .toTimeString()
      .split(" ")[0]
      .slice(0, 5)}`;
  };

  const getCoreData = () => {
    setIsLoading(true);

    // Get the experience and the experience summary and then set isLoading to false. Do this in parallel.
    Promise.all([
      getExperience(companyId!, experienceId!),
      getExperienceSummary(companyId!, experienceId!),
    ])
      .then(([experience, summary]) => {
        setExperienceSummary(summary);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error getting experience data:", error);
        setIsLoading(false);
      });
  };

  const handleInputChange = (e: any) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      date: value,
    }));
  };

  const formatSelectedDate = () => {
    const selectedDate: Date = new Date(formData.date);
    const formattedDate = selectedDate.toDateString();
    return formattedDate;
  }

  const formatDuration = (duration: number): string => {

    // The duration is in milliseconds
    const totalSeconds = Math.floor(duration / 1000);

    const hours = Math.floor(totalSeconds / 3600);

    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  }


  useEffect(() => {
    getCoreData();
  }, []);

  useEffect(() => {
    if (experienceSummary && formData.date) {
      getHourlyData();
    }
  }, [experienceSummary, formData.date]);

  if (isLoading) {
    return (
      <section>
        <BackButton />
        <PageTitle>
          Loading data <LoadingEllipsis />
        </PageTitle>
        <p className="text-gray-300">
          Please wait while we load the data.
          <LoadingEllipsis />
        </p>
      </section>
    );
  }

  if (!experienceSummary) {
    return (
      <section>
        <BackButton />
        <PageTitle>Experience not found</PageTitle>
        <p className="text-gray-300">
          The experience you are looking for does not exist.
        </p>
      </section>
    );
  }

  return (
    <section role="content">
      <BackButton />
      <PageTitle>
        {experienceSummary?.name} streaming and recording data
      </PageTitle>
      <div className="grid grid-cols-1 gap-4 mt-4">
        <h2 className="text-2xl fill">Latest summary</h2>
        <ExperienceAnalyticsBlock experience={experienceSummary!} navigatable={false} />
      </div>
      <hr className="text-gray-300 mt-5 mb-5" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-2">
        <h2 className="text-2xl fill">Daily drilldown for {formatSelectedDate()}</h2>
        <input
          type="date"
          name="chosenDate"
          placeholder="select date"
          value={formData.date}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border rounded text-black"
        />
      </div>
      {!isGettingHourlyData && hourlyDataSummary && (
      <div className="card mt-4 mb-2 p-4">
        <h3 className="text-xl fill">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-2">
          <div className="flex flex-col">
            <span className="text-gray-300">Total streaming sessions</span>
            <span>{hourlyDataSummary.totalSessions}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-300">Total local recordings</span>
            <span>{hourlyDataSummary.totalLocalRecordings}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-300">Average streaming session duration</span>
            <span>{formatDuration(hourlyDataSummary.averageSessionDuration)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-300">Average local recording duration</span>
            <span>{formatDuration(hourlyDataSummary.averageLocalRecordingDuration)}</span>
          </div>
        </div>
      </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4 mb-2">
        <div>
          <h3 className="text-xl fill">Streaming sessions</h3>

          <table className="table-auto w-full card border-spacing-2">
            <thead>
              <tr className="">
                <th className="font-normal text-left p-4">Time</th>
                <th>Count</th>
                <th>Average duration</th>
              </tr>
            </thead>
            <tbody>
              {isGettingHourlyData &&
                Array.from({ length: 3 }, (_, i) => (
                  <tr className="border-t border-cyan-900" key={i}>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                  </tr>
                ))}

              {!isGettingHourlyData && hourlyData && (
                <>
                  {!hourlyData.sessions || hourlyData.sessions.length === 0 ? (
                    <tr className="border-t border-cyan-900">
                      <td className="text-left p-4" colSpan={3}>
                        No results found.
                      </td>
                    </tr>
                  ) : (
                    hourlyData.sessions.map(
                      (hourItem: ExperienceAnalyticsSessionsByHour, i) => (
                        <tr key={i}>
                          <td className="text-left p-4">
                            {formatDateAsTime(hourItem.date)}
                          </td>
                          <td className="text-left p-4">{hourItem.count}</td>
                          <td className="p-4 w-auto text-right">
                            {formatDuration(hourItem.averageDuration)}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div>
          <h3 className="text-xl fill">Local recordings</h3>

          <table className="table-auto w-full card border-spacing-2">
            <thead>
              <tr className="">
                <th className="font-normal text-left p-4">Time</th>
                <th>Count</th>
                <th>Average duration</th>
              </tr>
            </thead>
            <tbody>
              {isGettingHourlyData &&
                Array.from({ length: 3 }, (_, i) => (
                  <tr className="border-t border-cyan-900" key={i}>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                    <td className="text-left p-4">
                      <Ghost additionalClasses="p-4 rounded" />
                    </td>
                  </tr>
                ))}

              {!isGettingHourlyData && hourlyData && (
                <>
                  {!hourlyData.localRecordings || hourlyData.localRecordings.length === 0 ? (
                    <tr className="border-t border-cyan-900">
                      <td className="text-left p-4" colSpan={3}>
                        No results found.
                      </td>
                    </tr>
                  ) : (
                    hourlyData.localRecordings.map(
                      (hourItem: ExperienceAnalyticsLocalRecordingsByHour, i) => (
                        <tr key={i}>
                          <td className="text-left p-4">
                            {formatDateAsTime(hourItem.date)}
                          </td>
                          <td className="text-left p-4">{hourItem.count}</td>
                          <td className="p-4 w-auto text-right">
                            {formatDuration(hourItem.averageDuration)}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
